import type { Component } from '../router';
import { ensureMotifDefs, frondField, curl } from '../components/shop/frondArt';
import { fdaDisclaimer, withClaimMarker } from '../components/shop/disclaimer';
import { t, applyHead } from '../i18n/locale';

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

function link(className: string, text: string, href: string): HTMLAnchorElement {
  const a = el('a', className, text) as HTMLAnchorElement;
  a.href = href;
  return a;
}

// The Asmaluz front door. Built under the one-motif direction (the Dicksonia
// sellowiana frond), reusing the storefront system from FE-002b. All copy flows
// through i18n `t()` (CONTENT-003) so it renders EN/ES/PT; static otherwise —
// no API dependency — so the brand story renders for any visitor.
class HomeView implements Component {
  private root: HTMLElement | null = null;

  mount(host: HTMLElement): void {
    ensureMotifDefs();
    applyHead({ titleKey: 'head.home.title', descKey: 'head.home.desc' });
    const wrap = el('div');
    const main = el('div', 'home');
    main.append(
      this.hero(),
      this.trust(),
      this.science(),
      this.line(),
      this.story(),
      this.faq(),
      this.cta(),
    );
    wrap.append(main, this.compliance());
    host.appendChild(wrap);
    this.root = wrap;
  }

  unmount(): void {
    this.root?.remove();
    this.root = null;
  }

  private shopCta(): HTMLAnchorElement {
    const a = link('buy-btn', '', '/products');
    a.append(curl('buy-btn__curl'), document.createTextNode(` ${t('common.shop')}`));
    return a;
  }

  private hero(): HTMLElement {
    const hero = el('section', 'home__hero');

    const copy = el('div', 'home__hero-copy');
    copy.append(
      el('h1', 'home__headline', t('home.hero.title')),
      el('p', 'home__sub', t('home.hero.sub')),
    );
    const ctas = el('div', 'home__cta-row');
    ctas.append(this.shopCta(), link('buy-link', t('home.hero.science'), '#science'));
    copy.appendChild(ctas);
    copy.appendChild(fdaDisclaimer());

    const media = el('div', 'home__media');
    media.appendChild(frondField('liquid'));

    hero.append(copy, media);
    return hero;
  }

  private trust(): HTMLElement {
    const strip = el('section', 'home__trust');
    strip.setAttribute('aria-label', 'Product qualities');
    const keys = [
      'home.trust.polyphenols',
      'home.trust.cznc',
      'home.trust.vegan',
      'home.trust.gluten',
      'home.trust.gmp',
      'home.trust.usa',
    ];
    for (const key of keys) {
      const s = el('span');
      s.append(curl('home__trust-curl'), document.createTextNode(` ${t(key)}`));
      strip.appendChild(s);
    }
    return strip;
  }

  private science(): HTMLElement {
    const section = el('section', 'home-section');
    section.id = 'science';
    const head = el('div', 'home-section__head');
    head.append(
      el('h2', 'home-section__title', t('home.science.title')),
      el('p', 'home-section__lede', t('home.science.lede')),
    );
    section.appendChild(head);

    const grid = el('div', 'proof-grid');
    const tiles: [string, string, boolean][] = [
      [t('home.proof.decades.k'), t('home.proof.decades.v'), true],
      ['ANVISA', t('home.proof.anvisa.v'), false],
      ['C + Zn', t('home.proof.czn.v'), false],
    ];
    for (const [k, v, lead] of tiles) {
      const tile = el('div', lead ? 'proof-tile proof-tile--lead' : 'proof-tile');
      tile.append(el('span', 'proof-tile__k', k), el('span', 'proof-tile__v', v));
      grid.appendChild(tile);
    }
    section.appendChild(grid);
    return section;
  }

  private line(): HTMLElement {
    const section = el('section', 'home-section');
    const head = el('div', 'home-section__head');
    head.appendChild(el('h2', 'home-section__title', t('home.line.title')));
    section.appendChild(head);

    const grid = el('div', 'home-line');
    grid.append(
      this.card('liquid', t('home.line.liquid.form'), 'Asmaluz™ C+', t('home.line.liquid.claim'), '$39'),
      this.card('tablets', t('home.line.tablets.form'), t('home.line.tablets.name'), t('home.line.tablets.claim'), '$34'),
    );
    section.appendChild(grid);
    return section;
  }

  private card(form: 'liquid' | 'tablets', formLabel: string, name: string, claim: string, price: string): HTMLElement {
    const card = link('product-card', '', '/products');
    const media = el('div', 'product-card__media');
    media.appendChild(frondField(form));
    const body = el('div', 'product-card__body');
    body.append(
      el('span', 'product-card__form', formLabel),
      el('h3', 'product-card__name', name),
      el('p', 'product-card__claim', withClaimMarker(claim)),
    );
    const foot = el('div', 'product-card__foot');
    const p = el('span', 'price');
    p.append(document.createTextNode(`${price} `), el('span', 'price__sample', t('common.sample')));
    foot.append(p, el('span', 'buy-link', t('home.line.view')));
    body.appendChild(foot);
    card.append(media, body);
    return card;
  }

  private story(): HTMLElement {
    const section = el('section', 'home-section');
    const story = el('div', 'home-story');
    story.append(
      el('p', 'home-story__q', t('home.story.q')),
      el('p', 'home-story__body', t('home.story.body')),
    );
    story.appendChild(el('span', 'home-story__who', t('home.story.who')));
    section.appendChild(story);
    return section;
  }

  private faq(): HTMLElement {
    const section = el('section', 'home-section');
    const head = el('div', 'home-section__head');
    head.appendChild(el('h2', 'home-section__title', t('home.faq.title')));
    section.appendChild(head);

    const faq = el('div', 'faq');
    const qas: [string, string][] = [
      [t('home.faq.q1'), t('home.faq.a1')],
      [t('home.faq.q2'), t('home.faq.a2')],
      [t('home.faq.q3'), t('home.faq.a3')],
    ];
    for (const [q, a] of qas) {
      const item = el('details', 'faq__item');
      item.setAttribute('name', 'home-faq');
      const summary = el('summary', 'faq__q');
      const sign = el('span', 'faq__sign');
      sign.setAttribute('aria-hidden', 'true');
      summary.append(document.createTextNode(q), sign);
      item.append(summary, el('p', 'faq__a', a));
      faq.appendChild(item);
    }
    section.appendChild(faq);
    return section;
  }

  private cta(): HTMLElement {
    const section = el('section', 'home-section');
    const tile = el('div', 'cta-tile');
    tile.appendChild(curl('cta-tile__curl'));
    tile.append(
      el('h2', 'cta-tile__title', t('home.cta.title')),
      el('p', 'cta-tile__sub', t('home.cta.sub')),
    );
    tile.appendChild(this.shopCta());
    section.appendChild(tile);
    return section;
  }

  private compliance(): HTMLElement {
    const footer = el('footer', 'compliance');
    const inner = el('div', 'compliance__inner');
    inner.append(
      el('p', 'compliance__mark', 'Asmaluz'),
      el('p', 'compliance__text', `*${t('common.fda')} ${t('compliance.extra')}`),
      el('p', 'compliance__meta', t('compliance.made')),
    );
    footer.appendChild(inner);
    return footer;
  }
}

export function HomePage(): Component {
  return new HomeView();
}
